import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { LonaIcons, CheckmarkIcon } from "@tento-ui/ui/icons";

@component({
  name: "task-status-indicator",
})
export class TaskStatusIndicator extends LonaWebComponent {
  bind(
    status?: Option<"completed" | "inProgress" | "planned" | "autoScheduled">,
  ) {
    this.setAttribute("status", status || "planned");

    // Update checkmark icon for completed status
    const $checkmark = this.$("checkmark");
    if (status === "completed") {
      $checkmark.replaceChildren(
        LonaIcons.make(CheckmarkIcon, { sizePx: 16, color: "white" }),
      );
    } else {
      $checkmark.replaceChildren();
    }

    // Update aria label for accessibility
    const label = this.getStatusLabel(status);
    this.setAttribute("aria-label", label);
    this.setAttribute("title", label);
  }

  private getStatusLabel(
    status?: Option<"completed" | "inProgress" | "planned" | "autoScheduled">,
  ): string {
    if (!status) return "Planned";
    switch (status) {
      case "completed":
        return "Completed";
      case "inProgress":
        return "In Progress";
      case "planned":
        return "Planned";
      case "autoScheduled":
        return "Auto Scheduled";
      default:
        return status;
    }
  }

  static $styles = [
    css`
      :host {
        display: inline-block;
        width: 24px;
        height: 24px;
        cursor: pointer;
        position: relative;
        vertical-align: middle;
        border-radius: 4px;
        transition: background-color 0.2s ease;
      }

      :host(:hover) {
        background-color: var(--bg-hover);
      }

      #circle {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 1px solid;
        background: transparent;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        transition: all 0.2s ease;
      }

      /* Empty circle for planned/not started */
      :host([status="planned"]) #circle {
        border-color: var(--gray);
        background: transparent;
      }

      /* Half circle for in progress - filled from bottom to top */
      :host([status="inProgress"]) #circle {
        background: linear-gradient(
          to top,
          var(--blue) 0%,
          var(--blue) 50%,
          transparent 50%,
          transparent 100%
        );
        border-color: var(--blue);
      }

      /* Full circle for completed */
      :host([status="completed"]) #circle {
        background: var(--bg-icon-inv);
        border-color: var(--fg-icon-ter);
      }

      /* Scheduled - dotted border */
      :host([status="autoScheduled"]) #circle {
        border-style: dashed;
        border-color: var(--purple);
      }

      #checkmark {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `,
  ];

  static $html = html`
    <div id="circle">
      <div id="checkmark"></div>
    </div>
  `;
}
